import { observer } from 'mobx-react';
import React, { ChangeEventHandler, FC, useCallback, useMemo } from 'react';
import { Badge, Form, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IconButton } from 'src/libs/ui-kit/buttons/IconButton/IconButton';
import { ExpandableWidgetTable } from 'src/libs/ui-kit/expandable-widget-table/ExpandableWidgetTable';
import { ExpandableRowsContainer } from 'src/libs/ui-kit/expandable-widget-table/ExpandableRowsContainer';
import { useStore } from 'src/modules/users/hooks/useStore';
import { UserAddressesWidgetColumn } from '../../interfaces';
import { IUserAddressesListItem } from '../../service-provider';
import { UserAddressesStoreContext } from '../../UserAddresses.widget';

import styles from './styles.module.scss';

export interface UserAddressesTableProps {
  selectable?: boolean;
  selected?: string | null;
  columns: readonly UserAddressesWidgetColumn[];
  disableDeletion: boolean;
  disableSelection: boolean;
  readonly: boolean;
  previewRowsCount?: number;

  onSelect?(value: string): void;
}

const stopPropagationHandler = <T extends { stopPropagation: () => void }>(event: T) => event.stopPropagation();

namespace SelectAddressRadioGroupNameSuffix {
  let id = 0;

  export function getUnique() {
    id += 1;
    return id;
  }
}

export const UserAddressesTable: FC<UserAddressesTableProps> = observer(
  ({ columns, selectable, selected, onSelect, disableSelection, disableDeletion, readonly, previewRowsCount }) => {
    const store = useStore(UserAddressesStoreContext);

    const nameSuffix = useMemo(() => SelectAddressRadioGroupNameSuffix.getUnique(), []);

    const columnsMap = useMemo(
      () =>
        columns.reduce((acc, column) => {
          acc[column] = true;
          return acc;
        }, {} as Record<UserAddressesWidgetColumn, undefined | true>),
      [columns],
    );

    const UserAddressesTableRow = (address) => (
      <Row
        address={address}
        columnsMap={columnsMap}
        disableDeletion={disableDeletion}
        disableSelection={disableSelection}
        readonly={readonly}
        onSelect={onSelect}
        selectable={selectable}
        selected={selected}
        nameSuffix={nameSuffix}
      />
    );

    return (
      <div className="px-5">
        <ExpandableWidgetTable previewRowsCount={previewRowsCount} total={store.addresses.length}>
          <thead>
            <tr>
              {columnsMap[UserAddressesWidgetColumn.Address] && <th className="border-0 text-tiny pt-0">Адрес</th>}
              {columnsMap[UserAddressesWidgetColumn.Name] && <th className="border-0 text-tiny pt-0">Название</th>}
              {columnsMap[UserAddressesWidgetColumn.Service] && (
                <th className="border-0 text-tiny pt-0">Обслуживание</th>
              )}
              {columnsMap[UserAddressesWidgetColumn.Deleted] && <th className="border-0 text-tiny pt-0">Удалён</th>}
            </tr>
          </thead>

          <tbody>
            <ExpandableRowsContainer
              data={store.addresses.slice().sort((a) => (a.id === selected ? -1 : 1))}
              keyBy="id"
              Component={UserAddressesTableRow}></ExpandableRowsContainer>
          </tbody>
        </ExpandableWidgetTable>
      </div>
    );
  },
);

interface RowProps {
  address: IUserAddressesListItem;
  selectable?: boolean;
  selected?: string | null;
  disableSelection: boolean;

  onSelect?(value: string): void;

  columnsMap: Record<UserAddressesWidgetColumn, true | undefined>;
  readonly: boolean;
  disableDeletion: boolean;
  nameSuffix: number;
}

const Row = observer(
  ({
    address,
    columnsMap,
    disableDeletion,
    disableSelection,
    selectable,
    selected,
    onSelect,
    readonly,
    nameSuffix,
  }: RowProps) => {
    const store = useStore(UserAddressesStoreContext);

    const onClickCallback = useCallback(() => store.selectAddressForViewing(address.id), [address.id, store]);

    const onSelectCallback = useCallback<ChangeEventHandler<HTMLInputElement>>(
      (e) => {
        e.stopPropagation();
        if (onSelect && address.id) onSelect(address.id);
      },
      [address.id, onSelect],
    );

    const checked = !!selected && selected === address.id;

    return (
      <tr>
        {columnsMap[UserAddressesWidgetColumn.Address] && (
          <td className="text-base">
            <div className="d-flex cursor-pointer" onClick={onClickCallback}>
              {selectable && (
                <div className="mr-1">
                  <Form.Check
                    type="radio"
                    name={`order-address-${nameSuffix}`}
                    disabled={disableSelection || readonly || !!address.isDeleted}
                    checked={checked}
                    onChange={onSelectCallback}
                    onClick={stopPropagationHandler}
                  />
                </div>
              )}

              <div>
                <div>{address.value}</div>
                <div className={styles.city}>{address.city}</div>
              </div>
            </div>
            <span className="text-align-center text-muted">
              UUID&nbsp;–&nbsp;<span className="user-select-all cursor-pointer">{address.id}</span>
            </span>
          </td>
        )}

        {columnsMap[UserAddressesWidgetColumn.Name] && (
          <td className="text-base cursor-pointer" onClick={onClickCallback}>
            {address?.label}
          </td>
        )}

        {columnsMap[UserAddressesWidgetColumn.Service] && (
          <td className="text-base cursor-pointer" onClick={onClickCallback}>
            {Object.hasOwn(address, 'serviceAvailable') &&
              (address.serviceAvailable ? <Badge variant="success">Да</Badge> : <Badge variant="danger">Нет</Badge>)}
          </td>
        )}

        {columnsMap[UserAddressesWidgetColumn.Deleted] && (
          <td>
            <Badge variant={address.isDeleted ? 'danger' : 'default'}>{address.isDeletedString}</Badge>
          </td>
        )}

        {columnsMap[UserAddressesWidgetColumn.Actions] && (
          <>
            <td className="pr-0">
              {address.needsToComplete ? (
                <OverlayTrigger
                  placement="left"
                  overlay={
                    <Tooltip id={`address-${address.id}-complete-tooltip`}>
                      <div className="mb-1 text-left">Дополнить адрес</div>
                      <div className="text-left">Необходимо указать подъезд, этаж и домофон</div>
                    </Tooltip>
                  }>
                  <div className={styles.warning_button}>
                    <IconButton
                      size="sm"
                      icon="pen"
                      variant="warning"
                      clickHandler={() => store.toggleEditAddressModal(address.id)}
                      disabled={readonly}
                      className="warning-round"
                    />
                  </div>
                </OverlayTrigger>
              ) : (
                <IconButton
                  size="sm"
                  icon="pen"
                  clickHandler={() => store.toggleEditAddressModal(address.id)}
                  disabled={readonly}
                />
              )}
            </td>

            <td className="pl-1">
              {address.isDeleted ? (
                <IconButton
                  icon="trash-arrow-up"
                  size="sm"
                  clickHandler={() => store.restoreAddress(address)}
                  isLoading={store.awaitingChangingAddresses.has(address.id!)}
                  disabled={readonly || disableDeletion}
                />
              ) : (
                <IconButton
                  icon="trash"
                  size="sm"
                  clickHandler={() => store.deleteAddress(address)}
                  isLoading={store.awaitingChangingAddresses.has(address.id!)}
                  disabled={readonly || disableDeletion || checked}
                />
              )}
            </td>
          </>
        )}
      </tr>
    );
  },
);
